import {
    IPsoDiscountConfiguration,
    IPsoMarketConfiguration,
    IPsoMarketSeasonConfiguration,
    PsoSeasonIntendedForEnum
} from "../configuration/objects/pso-markets-configuration.interface";
import {BookingModel} from "../booking/models/booking.model";
import {IDotRezBookingSession} from "../dot-rez-api/session/booking-session/dot-rez-booking.session.interface";
import {IDotRezBookingData} from "../dot-rez-api/data-contracts/booking/booking-state/booking-state.data-contracts";
import {IApplyPsoDiscountFeeResponse} from "../airline-webapi/responses/apply-pso-discount-fee.response";
import {PassengerFeeModel} from "../booking/models/fees/passenger/passenger-fee.model";
import {IServiceFactory} from "../service-factory.interface";
import {NullableNumber, NullableString} from "../../types/nullable-types";
import {
    BookingSessionClassesOfService
} from "../dot-rez-api/session/booking-session/booking-session-classes-of-service";
import {PsoUserAgreementEnum, PsoUserOptionsEnum} from "../flight-search/flight-search-controller/pso.enums";
import {
    InferPsoUserOptionsFromBookingDataResult,
    IPsoMarket,
    IPsoMarketDiscountOptions,
    IPsoMarketSeason,
    IPsoMarketTranslations
} from "./pso-market.interface";
import {IPassengerPsoCategoryViewModel} from "../booking/models/passenger/passenger-pso-category.view-model.interface";
import {
    AdultPassengerPsoCategory,
    ChildPassengerPsoCategory
} from "../booking/models/passenger/passenger-pso-category.model";

enum PsoMarketName {
    Sardinia = "sardinia",
    Marche = "marche",
    Sicilia = "sicilia"
}

export class PsoMarket implements IPsoMarket {
    constructor(private readonly configuration: IPsoMarketConfiguration,
                private readonly services: IServiceFactory) {
        this._seasons = (configuration.seasons ?? []).map(seasonConfig => new PsoMarketSeason(seasonConfig, services));
        this.translations = new PsoMarketTranslations(configuration, services);
    }

    private readonly _seasons: PsoMarketSeason[];
    readonly translations: PsoMarketTranslations;

    get id(): number {
        return this.configuration.id;
    }

    get marketName(): string {
        return this.configuration.marketName;
    }

    get shouldCollectPassengerCategory(): boolean {
        return this.marketName.toLowerCase() === PsoMarketName.Sardinia.toLowerCase();
    }

    getAgentForUserOption(posUserOption: PsoUserOptionsEnum): NullableString {
        switch (posUserOption) {
            case PsoUserOptionsEnum.Resident:
                return this.configuration.agent;
            default:
                return this.getAgentForSeason(posUserOption)

        }
    }

    getAdultPassengerCategories(psoUserOption: PsoUserOptionsEnum): IPassengerPsoCategoryViewModel[] {
        if(!this.shouldCollectPassengerCategory) {
            return [];
        }

        switch (psoUserOption) {
            case PsoUserOptionsEnum.None: {
                return [];
            }
            case PsoUserOptionsEnum.Resident:
                return AdultPassengerPsoCategory.getCategoriesByIds(this.services, 1,2,3,4,5,6,7,8,9);
            default:
                return AdultPassengerPsoCategory.getCategoriesByIds(this.services, 10,11,12,13);

        }
    }

    getChildPassengerCategories(psoUserOption: PsoUserOptionsEnum): IPassengerPsoCategoryViewModel[] {
        if(!this.shouldCollectPassengerCategory) {
            return [];
        }

        switch (psoUserOption) {
            case PsoUserOptionsEnum.None: {
                return [];
            }
            case PsoUserOptionsEnum.Resident:
                return ChildPassengerPsoCategory.getCategoriesByIds(this.services, 101,102);
            default:
                return ChildPassengerPsoCategory.getCategoriesByIds(this.services, 101,102,103);

        }
    }

    getInfantPassengerCategories(psoUserOption: PsoUserOptionsEnum): IPassengerPsoCategoryViewModel[] {
        if(!this.shouldCollectPassengerCategory) {
            return [];
        }

        return ChildPassengerPsoCategory.getCategoriesByIds(this.services, 100);
    }


    getAgentForSeason(posUserOption: PsoUserOptionsEnum): NullableString {
        for(const season of this._seasons) {
            if(season.getPsoUserOption() === posUserOption) {
                return season.agent;
            }
        }
        return null;
    }

    getAgentForBundle(bundle: string): NullableString {
        if(this.configuration.bundles.includes(bundle)) {
            return this.configuration.agent;
        }

        return this._seasons.find(s => s.appliesToBundle(bundle))?.agent ?? null;
    }

    get allowNewBookings(): boolean {
        return this.configuration.allowNewBookings;
    }

    get bookDateStart(): Date {
        return this.services.time.parseIsoDate(this.configuration.bookDateStart);
    }

    get bookDateUntil(): Date {
        return this.services.time.parseIsoDate(this.configuration.bookDateUntil);
    }

    isApplicableAtBookingDate(bookingDate: Date): boolean {
        return this.bookDateStart.getTime() <= bookingDate.getTime() && bookingDate.getTime() <= this.bookDateUntil.getTime();
    }


    get departureDateStart(): Date {
        return this.services.time.parseIsoDate(this.configuration.departureDateStart);
    }

    get departureDateUntil(): Date {
        return this.services.time.parseIsoDate(this.configuration.departureDateUntil);
    }

    isApplicableAtDepartureDate(departureDate: Date): boolean {
        return this.departureDateStart.getTime() <= departureDate.getTime() && departureDate.getTime() <= this.departureDateUntil.getTime();
    }

    get allowReducedMobility(): boolean {
        return this.configuration.allowReducedMobility;
    }

    get allowStudent(): boolean {
        return Boolean(this.configuration.allowStudent);
    }

    get allowMedicalReason(): boolean {
        return Boolean(this.configuration.allowMedicalReason);
    }

    get allowRetired(): boolean {
        return Boolean(this.configuration.allowRetired);
    }


    get isUsingClassesOfService(): boolean {
        return this.configuration.useClassesOfService;
    }

    canBeAppliedForUserClassesOfService(userClassesOfService: BookingSessionClassesOfService): boolean {
        if(this.isUsingClassesOfService) {
            return userClassesOfService.pso(this.allPsoClassesOfService).length > 0
        }

        return true;
    }

    get classesOfServiceForResident(): string[] {
        if(this.isUsingClassesOfService) {
            return this.services.configuration.data.psoMarketsV2.classesOfService?.forResident ?? [];
        }
        return [];
    }


    getClassesOfServiceForSeason(season: PsoMarketSeason): string[] {
        if(this.isUsingClassesOfService) {
            return season.getClassesOfService();
        }

        return [];
    }

    get allPsoClassesOfService(): string[] {
        return [
            ...this.classesOfServiceForResident,
            ...this._seasons.selectMany(s => this.getClassesOfServiceForSeason(s))
        ].distinct(c => c);
    }

    getClassesOfServiceForSearch(userClassesOfService: BookingSessionClassesOfService, psoUserAgreement: PsoUserAgreementEnum, psoUserOption: PsoUserOptionsEnum): string[] {
        if(psoUserAgreement === PsoUserAgreementEnum.DontAgree) {
            return userClassesOfService.regular(this.allPsoClassesOfService);
        }

        if(psoUserOption === PsoUserOptionsEnum.Resident) {
            return userClassesOfService.pso(this.classesOfServiceForResident);
        }

        for(const season of this._seasons) {
            if(season.getPsoUserOption() === psoUserOption) {
                const seasonClasses = this.getClassesOfServiceForSeason(season);
                return userClassesOfService.pso(seasonClasses);
            }

        }

        return userClassesOfService.regular(this.allPsoClassesOfService);
    }


    getActiveSeasons(atDate: Date): PsoMarketSeason[] {
        return this._seasons.filter(s => s.enabled && s.startDate.getTime() <= atDate.getTime() && atDate.getTime() <= s.endDate.getTime());
    }

    get discountOptions(): PsoMarketDiscountOptions | null {
        if(this.configuration.discountOptions) {
            return new PsoMarketDiscountOptions(this.configuration.discountOptions, this.departureDateUntil);
        }

        return null;
    }

    getTotalPaxOverride(organizationCode: string, departureDate: Date): NullableNumber {
        const paxOverrideConfig = this.configuration.overrideTotalPaxCount;
        if(!paxOverrideConfig) {
            return null;
        }

        const minDate = this.services.time.addDays(this.services.time.currentDate, paxOverrideConfig.advancePurchaseInDays);

        if(departureDate.getTime() < minDate.getTime()) {
            return null;
        }

        if(!paxOverrideConfig.organizationCodes.includes(organizationCode)) {
            return null;
        }

        return paxOverrideConfig.paxCount;
    }

    inferPsoUserOptionsFromBookingData(bookingData: IDotRezBookingData): undefined | InferPsoUserOptionsFromBookingDataResult {
        const result = this._tryInferByClassesOfService(bookingData);
        if(result) {
            return result;
        }

        return this._tryInferByBundleType(bookingData);
    }

    private _tryInferByClassesOfService(bookingData: IDotRezBookingData): undefined | InferPsoUserOptionsFromBookingDataResult {
        if(!this.isUsingClassesOfService) {
            return undefined;
        }

        const bookingClasses = this._getBookingClasses(bookingData);
        if(bookingClasses.length === 0) {
            return undefined;
        }

        const classesForResident = this.classesOfServiceForResident;
        if(bookingClasses.some(c => classesForResident.includes(c))) {
            return {
                psoUserOption: PsoUserOptionsEnum.Resident,
                psoUserAgreement: PsoUserAgreementEnum.Agree
            };
        }

        for(const season of this._seasons) {
            const seasonClasses = this.getClassesOfServiceForSeason(season);
            if(bookingClasses.some(c => seasonClasses.includes(c))) {
                return {
                    psoUserOption: season.getPsoUserOption(),
                    psoUserAgreement: PsoUserAgreementEnum.Agree
                }
            }
        }

        return undefined;
    }

    private _tryInferByBundleType(bookingData: IDotRezBookingData): undefined | InferPsoUserOptionsFromBookingDataResult {
        const bookingBundles = bookingData.journeys.selectMany(j => j.segments.selectMany(s => s.passengerSegment.map(ps => ps.value.bundleCode)))
            .filter(bundleCode => Boolean(bundleCode))
            .distinct(bundleCode => bundleCode?.toString() ?? "");

        for(const bundle of bookingBundles) {
            if(this.configuration.bundles.includes(bundle ?? "")) {
                return {
                    psoUserOption: PsoUserOptionsEnum.Resident,
                    psoUserAgreement: PsoUserAgreementEnum.Agree
                };
            }

            const season = this._seasons.find(s => s.appliesToBundle(bundle!));
            if(season) {
                return {
                    psoUserOption: season.getPsoUserOption(),
                    psoUserAgreement: PsoUserAgreementEnum.Agree
                };
            }
        }

        return undefined;
    }

    private _getBookingClasses(bookingData: IDotRezBookingData): string[] {

        if(!bookingData.journeys) {
            return [];
        }

        let result: string[] = [];

        for(let journey of bookingData.journeys) {
            for(let segment of journey.segments) {
                for(let fare of segment.fares) {
                    result.push(fare.classOfService);
                }
            }
        }

        return result.distinct(c => c);
    }

    getCmsPageUrlParams(psoUserOption: PsoUserOptionsEnum): Record<string, string> {
        if(psoUserOption === PsoUserOptionsEnum.Resident) {
            return {
                market: this.configuration.marketName,
                intendedFor: "residents"
            };
        }

        for(const season of this._seasons) {
            if(season.getPsoUserOption() === psoUserOption) {
                return {
                    market: this.configuration.marketName,
                    intendedFor: season.intendedFor
                };
            }
        }

        return {
            market: this.configuration.marketName,
            intendedFor: "residents"
        };
    }
}

class PsoMarketSeason implements IPsoMarketSeason {
    constructor(private readonly seasonConfig: IPsoMarketSeasonConfiguration, private readonly services: IServiceFactory) {
    }

    get name(): string {
        return this.seasonConfig.name;
    }

    get enabled(): boolean {
        return this.seasonConfig.enabled;
    }

    appliesToBundle(bundle: string): boolean {
        return this.enabled && bundle === this.seasonConfig.bundle;
    }

    get agent(): string {
        return this.seasonConfig.agent;
    }

    get intendedFor(): PsoSeasonIntendedForEnum {
        return this.seasonConfig.intendedFor;
    }

    getPsoUserOption(): PsoUserOptionsEnum {
        switch (this.intendedFor) {
            case PsoSeasonIntendedForEnum.Families:
                return PsoUserOptionsEnum.Family;
            default:
                return PsoUserOptionsEnum.Worker;
        }
    }

    get startDate(): Date {
        if(this.seasonConfig.startDate) {
            return this.services.time.parseIsoDate(this.seasonConfig.startDate);
        } else {
            return new Date(1970, 1, 1);
        }
    }

    get endDate(): Date {
        if(this.seasonConfig.endDate) {
            return this.services.time.parseIsoDate(this.seasonConfig.endDate);
        } else {
            return new Date(9999, 12, 31);
        }
    }

    getClassesOfService(): string[] {
        switch (this.intendedFor) {
            case PsoSeasonIntendedForEnum.Families:
                return this.services.configuration.data.psoMarketsV2.classesOfService?.forFamily ?? [];
            default:
                return this.services.configuration.data.psoMarketsV2.classesOfService?.forWorker ?? [];
        }

    }

}

class PsoMarketDiscountOptions implements IPsoMarketDiscountOptions {
    constructor(private readonly discountConfig: IPsoDiscountConfiguration,
                public  readonly departureDateUntil: Date) {
    }

    async applyDiscount(booking: BookingModel): Promise<void> {
        await booking.mutationsManager.waitForMutations();

        const applyDiscountFeeResult = await booking.services.airlineWebapi.applyPsoDiscountFee({
            dotRezToken:  booking.token,
            journeysAmounts: booking.shoppingCart.journeysAllPurchases.map(j => {
                return {
                    key: j.journeyKey,
                    amount: j.grandTotalToDisplay.amount
                }
            })
        });


        const bookingData = await this._getBookingData(booking.session);

        if(bookingData) {
            booking.updateBookingSessionData({
                bookingData: bookingData
            });
        }

        await this._updateSsrs(booking, applyDiscountFeeResult);
    }

    private async _getBookingData(session: IDotRezBookingSession): Promise<IDotRezBookingData | undefined> {
        const {bookingData} = await session.bookingStateQueryBuilder().useBookingData().getBookingState();

        return bookingData;
    }


    hasPsoDiscountFee(booking: BookingModel): boolean {
        return Boolean(this._findFeeModel(booking));
    }

    private async _updateSsrs(booking: BookingModel, psoDiscountResult: IApplyPsoDiscountFeeResponse): Promise<void> {

        const ssrType = booking.services.ssrTypes.getSsrType(this.discountConfig.ssrCode);

        for(let journey of booking.unfilteredJourneys) {
            const hasDiscountFee = psoDiscountResult.journeysKeys.includes(journey.journeyKey);
            for(let ps of journey.getAllPassengersSegments()) {
                if(hasDiscountFee) {
                    ps.getSsr(ssrType).newQuantity = 1;
                } else {
                    ps.getSsr(ssrType).newQuantity = 0;
                }
            }
        }

        await booking.sellSsrs({skipDebounce: true});

        await booking.mutationsManager.waitForMutations();
    }

    private _findFeeModel(booking: BookingModel): PassengerFeeModel | null {
        for(let p of booking.passengers) {
            let f = p.fees.find(f => f.feeCode === this.discountConfig.feeCode) ?? null;
            if(f) {
                return f;
            }
        }
        return null;
    }

}

class PsoMarketTranslations implements IPsoMarketTranslations {
    constructor(private readonly configuration: IPsoMarketConfiguration,
                private readonly services: IServiceFactory) {
    }

    private get isSicily(): boolean {
        return this.configuration.marketName.toLowerCase() === 'sicilia';
    }

    getMarketNameTranslation(): string {
        switch (this.configuration.marketName.toLowerCase()) {
            case PsoMarketName.Sardinia.toLowerCase():
                return this.services.language.translate('Sardinia');
            case PsoMarketName.Marche.toLowerCase():
                return this.services.language.translate('Marche');
            case PsoMarketName.Sicilia.toLowerCase():
                return this.services.language.translate('Sicily');
            default:
                const marketName = this.configuration.marketName;
                return marketName[0].toUpperCase() + marketName.substring(1);
        }
    }

    get residentAndEquivalent(): string {
        return this.services.language.translate('Resident and equivalent');
    }

    getPsoUserOptionTranslation(psoUserOption: PsoUserOptionsEnum): string {
        switch (psoUserOption) {
            case PsoUserOptionsEnum.Resident:
                return this.iAmResidentInMarket;
            case PsoUserOptionsEnum.Worker:
                return this.iWorkInMarket;
            case PsoUserOptionsEnum.Family:
                return this.weAreAFamilyFromMarket;
            default:
                return this.iAmResidentInMarket;
        }
    }

    get iAmResidentInMarket(): string {
        if(this.isSicily) {
            return this.services.language.translate('I am resident in Sicily')
        } else {
            return this.services.language.translationFor('I am a resident in {market}').withParams({market: this.getMarketNameTranslation()});
        }
    }

    get iWorkInMarket(): string {
        if(this.isSicily) {
            return this.services.language.translate('I work in Sicily');
        } else {
            return this.services.language.translationFor('I work in {market}').withParams({market: this.getMarketNameTranslation()});
        }
    }

    get weAreAFamilyFromMarket(): string {
        switch (this.configuration.marketName.toLowerCase()) {
            case 'sardinia':
                return this.services.language.translate('I am a relative/legal guardian of a resident in Sardinia');
            case 'sicilia':
                return this.services.language.translate('I am a relative/legal guardian of a resident in Sicily');
            default:
                return "I am a parental";
        }
    }

    get areYouAResident(): string {
        if(this.isSicily) {
            return this.services.language.translate('Are you resident in Sicily?');
        } else {
            return this.services.language.translationFor('Are you a resident in {market}?').withParams({market: this.getMarketNameTranslation()})
        }
    }

    get reduceMobilitySsrDescription(): string {
        return this.services.language.translationFor(`Passenger with a civil disability equal to or greater than 80%, resident in {market}`)
            .withParams({
                market: this.getMarketNameTranslation()
            });
    }

    get residentInMarket(): string {
        if(this.isSicily) {
            return this.services.language.translate('Resident in Sicily');
        } else {
            return this.services.language.translationFor('Resident in {market}').withParams({market: this.getMarketNameTranslation()});
        }
    }

    get bornInMarket(): string {
        if(this.isSicily) {
            return this.services.language.translate('Born in Sicily');
        } else {
            return this.services.language.translationFor('Born in {market}').withParams({market: this.getMarketNameTranslation()});
        }
    }
}
